import decode from 'jwt-decode';
import http, { endpoints } from '../http';
import { persistLoginSettings } from './storage';
import { mapAuthenticatedUserFromDtos } from '../users/helpers';
const efktClient = http.createClient(endpoints.efkt);
/**
 * REAL — the whole domain. The Efkt fe-gateway serves the old webshop's sign-in and session surface
 * verbatim (`Efkt/Applications/Gateway/Frontend/Endpoints/CompatAuthenticationEndpoints.cs` +
 * `CompatUserEndpoints.cs`, merged 2026-09-10 in PR #3633): same paths, same query parameters, same
 * response shapes as datahub. There is no `/api/auth/*` namespace and nothing to translate — the
 * only difference between the two backends here is the base URL.
 *
 * Three consequences worth stating, because they are what make "call Efkt for everyone" safe:
 *
 * - **Login-URL discovery can be pointed at either backend.** Both build the URL from the same
 *   tenant, policy and client id, so the user lands in the same B2C journey and the resulting token
 *   validates against both. Two deliberate differences: Efkt sends no `ui_locales` (B2C picks the
 *   sign-in page's language from the browser), and it matches employee domains on `@esoft.com`
 *   rather than a bare suffix, so `anna@notesoft.com` correctly stays on the customer flow.
 * - **Efkt reports failure as an HTTP status, not as `error` inside a 200** — 400 for a missing or
 *   relative `redirectUrl`, 503 for a host with no customer sign-in configured. Callers have to
 *   handle both conventions while datahub is still in play.
 * - **The token is identical either way, so it cannot say which backend owns the caller.** Only
 *   `getAuthenticatedUser` can, and only by asking — see its note on the empty-profile signal.
 *
 * Deliberately **not** mirrored from datahub: `deauthenticate`/`persistToken`. `efkt/http.ts` exposes
 * no token write, and that is correct — token lifecycle belongs to the one shared cookie's one B2C
 * flow, not to per-backend API code.
 */
export default class AuthApi {
    static isAuthenticated() {
        return !!http.getAuthToken();
    }
    static getUserFromToken() {
        const token = http.getAuthToken();
        return token ? decode(token) : undefined;
    }
    /**
     * REAL — `GET /api/auth/migration-status`. Whether Efkt serves the signed-in customer, which is
     * what `apps/booqly/feature.flag.tsx` decides the component tree from.
     *
     * **Rejects on anything but a 200, and every rejection means "stay on retail-core"** — a 401 is
     * "not signed in" and never "not migrated", and a 500 is an unknowable verdict rather than a
     * false one. Neither is worth an error boundary: the caller has a correct answer to fall back to.
     *
     * An employee token answers `200 { isMigrated: false, resolution: 'NonCustomerSession' }` rather
     * than 401. Efkt honours no impersonation header, so an employee has no Efkt customer session to
     * be routed to.
     */
    static async getCustomerMigrationStatus() {
        const response = await efktClient.get('/api/auth/migration-status');
        return {
            isMigrated: response.data.isMigrated,
            resolution: response.data.resolution,
        };
    }
    /**
     * The session, from the same three reads the pre-Efkt client makes:
     * `GET /Authentication/user` → `GET /Authentication/user/branches` → `GET /User/id/{id}`.
     *
     * **Throws when Efkt authenticates the token but cannot identify the user**, which is the state
     * a customer whose Owner has not been flipped yet is in: authentication succeeds and the profile
     * read answers 200 with the `sub` alone and no email. Efkt returns it that way on purpose (a 401
     * risks a sign-in loop, and booqly dereferences the follow-up profile body unguarded), and it is
     * the only signal an unmigrated user produces — so turning it into a rejection here is what lets
     * `v2/modules/auth/hooks.ts` fall back to datahub. Without it the user authenticates fine and
     * simply sees nothing, with nothing looking broken.
     */
    static async getAuthenticatedUser() {
        if (!http.getAuthToken())
            return undefined;
        const { data: currentUser } = await efktClient.get('/Authentication/user');
        if (!(currentUser === null || currentUser === void 0 ? void 0 : currentUser.email)) {
            throw new Error('efkt: authenticated but not enrolled — no profile to build a session from');
        }
        const userId = currentUser.currentlyImpersonatingUserId || currentUser.id;
        const [branches, profile] = await Promise.all([
            efktClient.get('/Authentication/user/branches'),
            efktClient.get(`/User/id/${encodeURIComponent(userId)}`),
        ]);
        return mapAuthenticatedUserFromDtos(currentUser, profile.data, branches.data || []);
    }
    /**
     * Customer login-URL discovery, and the localStorage writes the rest of the flow reads back.
     * `appRoute` is a path on this origin — the absolute redirect is composed here, as datahub's
     * equivalent does, because it has to match the URI whitelisted in the Azure app registration.
     */
    static async b2cAuth(email, appRoute) {
        const response = await efktClient.get('/Authentication/login-url', {
            params: { redirectUrl: `${window.location.origin}${appRoute}`, email, nonce: 'esoft-test' },
        });
        persistLoginSettings(response.data, { email, viaAdJourney: false });
        return response.data;
    }
    /** Employee/AD login-URL discovery. Same contract, same storage keys. */
    static async esoftAuth(appRoute) {
        const response = await efktClient.get('/Authentication/login-esoft-url', {
            params: { redirectUrl: `${window.location.origin}${appRoute}`, nonce: 'esoft-test' },
        });
        persistLoginSettings(response.data, { viaAdJourney: true });
        return response.data;
    }
}
